import {
  DOCUMENT_REDACTION_EXTENSIONS,
  DOCUMENT_REDACTION_MIME_TYPES,
} from './constants';

const FILE_ITEM_SELECTOR = '[data-testid="fileItem"]';
const FILE_NAME_SELECTOR = '[data-testid="itemNameText"]';

// File type is determined by extension rather than MIME type because glc-react
// does not expose the underlying File objects WHILE THEY ARE STAGED — only the
// rendered filename text in the DOM is accessible. (It does pass a real `File[]`
// to `onPickFiles` at submit; that is too late for anything that has to answer
// as the user builds the selection, which is what these helpers are for.) Previously, native File objects were
// tracked via input[change] and drop events, which provided file.type (MIME).
// That approach only captured the most-recent selection, not the full accumulated
// list that glc-react manages internally. The DOM-scraping approach fixes that
// correctness bug at the cost of MIME-based detection. Extensionless files will
// not be recognized as images regardless of their actual content.
// cspell:ignore jfif pjpeg apng svgz
const IMAGE_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.bmp',
  '.webp',
  '.svg',
  '.ico',
  '.tiff',
  '.tif',
  // JPEG variants
  '.jfif',
  '.pjpeg',
  '.pjp',
  // PNG animated
  '.apng',
  // Modern formats
  '.avif',
  '.heic',
  '.heif',
  '.jxl',
  // Vector / icon
  '.svgz',
  '.cur',
  // JPEG 2000
  '.jp2',
  '.j2k',
  '.jpx',
  '.j2c',
  // Camera raw
  '.raw',
  '.cr2',
  '.cr3',
  '.nef',
  '.nrw',
  '.arw',
  '.dng',
  '.orf',
  '.rw2',
  '.pef',
  '.srw',
  '.raf',
  '.3fr',
  '.mrw',
];

export const isImageByExtension = (filename: string): boolean => {
  if (!filename) {
    return false;
  }
  const lowerFilename = filename.toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => lowerFilename.endsWith(ext));
};

/**
 * Scrape the trimmed filename text of every file currently staged in the picker.
 *
 * Exported because callers that need more than one fact about the staged list
 * should scrape ONCE and derive the rest from the array. Each predicate below
 * runs its own `querySelectorAll`, which was free when the picker held at most
 * five files and is four redundant passes over the list now that it holds up to
 * thirty (VE-28989) — under a MutationObserver that fires on every change.
 */
export const getStagedFilenames = (root: ParentNode = document): string[] =>
  Array.from(root.querySelectorAll<HTMLElement>(FILE_ITEM_SELECTOR))
    .map(
      (item) =>
        item
          .querySelector<HTMLElement>(FILE_NAME_SELECTOR)
          ?.textContent?.trim() ?? ''
    )
    .filter(Boolean);

/** Every name is an image, and there is at least one. Pure; see `getStagedFilenames`. */
export const areAllImageFilenames = (filenames: readonly string[]): boolean =>
  filenames.length > 0 && filenames.every(isImageByExtension);

export const areAllPickerFilesImages = (root: ParentNode = document): boolean =>
  areAllImageFilenames(getStagedFilenames(root));

export const isDocumentByExtension = (filename: string): boolean => {
  if (!filename) {
    return false;
  }
  const lowerFilename = filename.toLowerCase();
  return (DOCUMENT_REDACTION_EXTENSIONS as readonly string[]).some((ext) =>
    lowerFilename.endsWith(ext)
  );
};

/**
 * True when a file is a redaction document type, by MIME type or filename
 * extension. Use when only the file's metadata is available (e.g. at TDO
 * create time) rather than the picker DOM.
 *
 * Related, deliberately separate: `isPdfMedia`
 * (common-app/state/modules/engines/ingestion/services.ts) answers the narrower
 * "is this a PDF", and is shared by the fast-upload and converter-skip paths so
 * those two cannot disagree about a file. This helper covers every document
 * type, for the settings-omit path.
 *
 * DOC-TODO: `isDocumentRedactionJob` (common-app/state/modules/engines/
 * ingestion/saga.ts) is a third near-duplicate of this one and could fold into
 * it.
 */
export const isDocumentFile = (
  fileType?: string | null,
  fileName?: string | null
): boolean => {
  const normalized = (fileType ?? '').split(';')[0]?.trim().toLowerCase();
  const mimeMatch =
    !!normalized &&
    (DOCUMENT_REDACTION_MIME_TYPES as readonly string[]).some(
      (m) => m.split(';')[0]?.trim().toLowerCase() === normalized
    );
  return mimeMatch || (fileName ? isDocumentByExtension(fileName) : false);
};

/** Every name is a document, and there is at least one. Pure; see `getStagedFilenames`. */
export const areAllDocumentFilenames = (
  filenames: readonly string[]
): boolean => filenames.length > 0 && filenames.every(isDocumentByExtension);

export const areAllPickerFilesDocuments = (
  root: ParentNode = document
): boolean => areAllDocumentFilenames(getStagedFilenames(root));

/** At least one name is a document. Pure; see `getStagedFilenames`. */
export const hasDocumentFilename = (filenames: readonly string[]): boolean =>
  filenames.some(isDocumentByExtension);

// True when at least one document has been added to the picker's file list.
export const hasPickerDocumentFile = (root: ParentNode = document): boolean =>
  hasDocumentFilename(getStagedFilenames(root));

/**
 * True when the picker's file list is EMPTY.
 *
 * The option rows dim to say "not applicable to what you staged", which is an
 * answer that needs a question: with nothing staged there is nothing to be
 * inapplicable to, and a dialog that opens with half its options greyed out
 * reads as half-broken rather than as waiting.
 *
 * `areAllPickerFilesDocuments` has always guarded on this — it returns false
 * for an empty list, so the media options stay live until a document actually
 * turns up. The document side had no equivalent and dimmed from the moment
 * the dialog opened. Same question, opposite direction, and only one of them
 * was being asked (VE-24948).
 */
export const hasNoPickerFiles = (root: ParentNode = document): boolean =>
  getStagedFilenames(root).length === 0;
